// /src/components/AIChat.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, ai } from '../api/axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import { v4 as uuidv4 } from 'uuid';
import { FaPaperPlane, FaSpinner, FaChevronDown, FaPlus, FaBuilding, FaSyncAlt } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa';
import MobileHeader from './layout/MobileHeader';
import MobileMenu from './layout/MobileMenu';
import { useTranslation } from 'react-i18next';
import TourMessage from './tours/TourMessage';

const NoPropertiesCard = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 p-4">
      <FaBuilding className="text-gray-600 text-6xl mb-4" />
      <h2 className="text-xl font-semibold text-white mb-2">
        {t('chat.noProperties.title')}
      </h2>
      <p className="text-gray-400 text-center mb-4">
        {t('chat.noProperties.description')}
      </p>
      <Link
        to="/properties"
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                 transition-colors duration-200 flex items-center"
      >
        <FaPlus className="mr-2" />
        {t('chat.noProperties.addProperty')}
      </Link>
    </div>
  );
};

const AIChat = ({ isGuest = false, guestPropertyId = null, guestToken = null, guestPropertyName = null, guestHostContact = null }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isConversationStarted, setIsConversationStarted] = useState(false);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const sliderRef = useRef(null);
  const actionsDropdownRef = useRef(null);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hostContact, setHostContact] = useState(null);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  // Per-device guest identifier so each device that scans the QR gets its
  // own conversation thread instead of all guests sharing one. Persisted in
  // localStorage so the same device picks up its conversation if it returns.
  const [guestDeviceId, setGuestDeviceId] = useState(() => {
    if (!isGuest) return null;
    let id = localStorage.getItem('guestDeviceId');
    if (!id) {
      id = uuidv4();
      localStorage.setItem('guestDeviceId', id);
    }
    return id;
  });
  const { t } = useTranslation();

  const predefinedQuestions = [
    "What's the check-in process?",
    "What tours are available?",
    "What amenities are included?",
    "Is parking available?",
    "How do I contact the host?"
  ];

  const fetchMessages = useCallback(async (threadIdToUse = threadId) => {
    if (!selectedPropertyId || !threadIdToUse) return;
    setIsFetchingMessages(true);
    try {
      const response = await api.get('/conversation/history', {
        params: {
          page,
          propertyId: selectedPropertyId,
          threadId: threadIdToUse,
          ...(isGuest && guestDeviceId ? { deviceId: guestDeviceId } : {}),
        },
        headers: isGuest
          ? { Authorization: `Bearer ${guestToken}` }
          : { 'x-auth-token': localStorage.getItem('token') }
      });
  
      const newMessages = response.data.messages;
      if (newMessages.length === 0) {
        setHasMore(false);
        setIsFetchingMessages(false);
        setIsInitialLoading(false);
        return;
      }
      
      // Add IDs to any messages that don't have them
      const messagesWithIds = newMessages.map(msg => ({
        ...msg,
        id: msg.id || uuidv4()
      }));
      
      setMessages(prevMessages => {
        // Filter out duplicates by comparing content, type and timestamp
        const uniqueMessages = messagesWithIds.filter(
          newMsg => !prevMessages.some(
            prevMsg => prevMsg.content === newMsg.content && 
                      prevMsg.type === newMsg.type && 
                      prevMsg.timestamp === newMsg.timestamp
          )
        );
        
        // Combine messages and sort by timestamp (oldest first)
        const combinedMessages = [...prevMessages, ...uniqueMessages].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        
        return combinedMessages;
      });
      
      setHasMore(response.data.hasMore);
      if (response.data.hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    } catch (error) {
      console.error('Error fetching messages:', error.response?.data || error);
      setHasMore(false);
    } finally {
      setIsFetchingMessages(false);
      setIsInitialLoading(false);
    }
  }, [page, selectedPropertyId, threadId, isGuest, guestToken]);

  const fetchProperties = async () => {
    if (isGuest) return;
    setIsInitialLoading(true);
    try {
      const response = await api.get('/property/user/current', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      const allProperties = [...response.data.owned, ...response.data.managed];
      setProperties(allProperties);
      const storedPropertyId = localStorage.getItem('selectedPropertyId');
      if (storedPropertyId && allProperties.some(prop => prop._id === storedPropertyId)) {
        setSelectedPropertyId(storedPropertyId);
      } else if (allProperties.length > 0) {
        setSelectedPropertyId(allProperties[0]._id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setError('Failed to fetch properties. Please try again.');
    } finally {
      setIsInitialLoading(false);
    }
  };

  const fetchHostContact = async (propertyId) => {
    if (!propertyId) return;
    try {
      // Try to get property details which should include owner information
      const response = await api.get(`/property/${propertyId}`, {
        headers: isGuest
          ? { Authorization: `Bearer ${guestToken}` }
          : { 'x-auth-token': localStorage.getItem('token') }
      });

      // Check if property has owner information with phone
      if (response.data?.owner?.phone) {
        setHostContact({
          name: response.data.owner.fullName || response.data.owner.username || 'Host',
          phone: response.data.owner.phone
        });
      } else {
        setHostContact(null);
      }
    } catch (error) {
      console.error('Failed to fetch host contact:', error);
      setHostContact(null);
    }
  };

  // Move fetchOrCreateThread definition here, before it's used
  const fetchOrCreateThread = async () => {
    if (!selectedPropertyId) return;

    setIsInitialLoading(true);
    try {
      const headers = isGuest
        ? { Authorization: `Bearer ${guestToken}` }
        : { 'x-auth-token': localStorage.getItem('token') };

      const response = await ai.get('/start', {
        params: {
          propertyId: selectedPropertyId,
          userId: isGuest ? null : userId,
          ...(isGuest && guestDeviceId ? { deviceId: guestDeviceId } : {}),
        },
        headers
      });
  
      if (response.data.thread_id) {
        setThreadId(response.data.thread_id);
        setIsInitialLoading(false);
      } else {
        throw new Error('No thread ID received');
      }
    } catch (error) {
      console.error('Failed to start conversation:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to start conversation');
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (isGuest && guestPropertyId && guestToken) {
      setSelectedPropertyId(guestPropertyId);
      // Set host contact from prop if available
      if (guestHostContact) {
        setHostContact(guestHostContact);
      }
    } else if (!isGuest) {
      let uniqueUserId = localStorage.getItem('uniqueUserId');
      if (!uniqueUserId) {
        uniqueUserId = uuidv4();
        localStorage.setItem('uniqueUserId', uniqueUserId);
      }
      setUserId(uniqueUserId);
      
      fetchProperties();
    
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsDropdownOpen(false);
        }
        if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target)) {
          setIsActionsDropdownOpen(false);
        }
      };
    
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isGuest, guestPropertyId, guestToken]);
  
  // Second useEffect - property selection
  useEffect(() => {
    if (selectedPropertyId) {
      if (!isGuest) {
        localStorage.setItem('selectedPropertyId', selectedPropertyId);
        // Only fetch host contact for non-guest users
        // Guest users already have it from guestHostContact prop
        fetchHostContact(selectedPropertyId);
      }
      fetchOrCreateThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyId]);

  // NEW useEffect - fetching messages when thread is set
  useEffect(() => {
    if (threadId && selectedPropertyId) {
      fetchMessages(threadId);
    }
  }, [threadId, selectedPropertyId, fetchMessages]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (isInitialLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-900">
        <FaSpinner className="animate-spin text-white text-4xl mb-4" />
        <p className="text-white text-lg">{t('chat.loading')}</p>
      </div>
    );
  }

  if (!isGuest && !isInitialLoading && properties.length === 0) {
    return <NoPropertiesCard />;
  }

  const startNewThread = async () => {
    try {
      const response = await ai.get('/start', {
        params: {
          userId: isGuest ? null : userId,
          propertyId: selectedPropertyId,
          ...(isGuest && guestDeviceId ? { deviceId: guestDeviceId } : {}),
        },
        headers: isGuest ? { Authorization: `Bearer ${guestToken}` } : { 'x-auth-token': localStorage.getItem('token') }
      });
      const newThreadId = response.data.thread_id;
      setThreadId(newThreadId);
      if (!isGuest) {
        localStorage.setItem(`threadId_${userId}_${selectedPropertyId}`, newThreadId);
      }
      
      setMessages([]);
      setPage(1);
      setHasMore(true);
    } catch (error) {
      console.error('Failed to start conversation:', error.response?.data?.error || error.message);
      setMessages([{ type: 'error', content: error.response?.data?.error || 'Failed to start conversation. Please try again.', timestamp: new Date().toISOString() }]);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !selectedPropertyId) return;
    await sendMessage(input);
  };

  const sendMessage = async (message) => {
    setIsLoading(true);
    const userMessage = message;
    setInput('');
    
    const timestamp = new Date().toISOString();
    const tempId = uuidv4();
    
    // Add user message to the UI immediately with a temporary ID
    setMessages(prev => [...prev, { 
      id: tempId,
      type: 'user', 
      content: userMessage, 
      timestamp 
    }]);

    try {
      if (!threadId) {
        await startNewThread();
      }

      const chatResponse = await ai.post('/chat', {
        thread_id: threadId,
        message: userMessage,
        userId: isGuest ? null : userId,
        propertyId: selectedPropertyId,
        ...(isGuest && guestDeviceId ? { deviceId: guestDeviceId } : {}),
      }, {
        headers: isGuest ? { Authorization: `Bearer ${guestToken}` } : { 'x-auth-token': localStorage.getItem('token') }
      });
      
      const runId = chatResponse.data.run_id;
      if (chatResponse.data.thread_id !== threadId) {
        setThreadId(chatResponse.data.thread_id);
        if (!isGuest) {
          localStorage.setItem(`threadId_${userId}_${selectedPropertyId}`, chatResponse.data.thread_id);
        }
      }
      let status = 'in_progress';
      let aiResponse = '';

      while (status !== 'completed') {
        const checkResponse = await ai.post('/check', {
          thread_id: threadId,
          run_id: runId,
          userId: isGuest ? null : userId,
          propertyId: selectedPropertyId,
          ...(isGuest && guestDeviceId ? { deviceId: guestDeviceId } : {}),
        }, {
          headers: isGuest ? { Authorization: `Bearer ${guestToken}` } : { 'x-auth-token': localStorage.getItem('token') }
        });

        status = checkResponse.data.status;

        if (status === 'completed') {
          aiResponse = checkResponse.data.response;
          
          // Check if the response contains tour information - either via tools array or direct tour_request flag
          let hasTourInfo = false;
          let tourData = null;
          
          // Check for tour_request flag (new method)
          if (checkResponse.data.tour_request === true) {
            hasTourInfo = true;
            tourData = {
              keywords: checkResponse.data.tour_keywords || []
            };
          }
          // Also check for tools array (backward compatibility)
          else if (checkResponse.data.tools && Array.isArray(checkResponse.data.tools)) {
            const tourTool = checkResponse.data.tools.find(tool => tool.type === 'tour');
            if (tourTool) {
              hasTourInfo = true;
              tourData = tourTool.data;
            }
          }
          
          const aiTimestamp = new Date().toISOString();
          const aiMessageId = uuidv4();
          
          // Add the AI response
          setMessages(prev => [
            ...prev,
            { 
              id: aiMessageId, 
              type: 'ai', 
              content: aiResponse, 
              timestamp: aiTimestamp 
            }
          ]);
          
          // If there's tour information, add a tour message
          if (hasTourInfo && tourData) {
            setMessages(prev => [
              ...prev,
              {
                id: uuidv4(),
                type: 'tour',
                timestamp: new Date().toISOString(),
                // Convert any objects to strings to avoid React rendering issues
                tourData: JSON.stringify(tourData)
              }
            ]);
          }
          
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      await api.post('/conversation/save', {
        threadId,
        message: userMessage,
        response: aiResponse,
        propertyId: selectedPropertyId,
        ...(isGuest && guestDeviceId ? { deviceId: guestDeviceId } : {}),
      }, {
        headers: {
          ...(isGuest
            ? { Authorization: `Bearer ${guestToken}` }
            : { 'x-auth-token': localStorage.getItem('token') }
          )
        }
      });

    } catch (error) {
      console.error('Error in chat:', error.response?.data?.error || error.message);
      setMessages(prev => [...prev, { 
        id: uuidv4(),
        type: 'error', 
        content: 'An error occurred. Please try again.', 
        timestamp: new Date().toISOString() 
      }]);
      
      if (error.response && error.response.status === 404) {
        await startNewThread();
      }
    } finally {
      setIsLoading(false);
      setIsConversationStarted(true);
    }
  };

  const handlePropertyChange = (propertyId) => {
    // Don't do anything if the same property is selected
    if (propertyId === selectedPropertyId) {
      setIsDropdownOpen(false);
      return;
    }

    setSelectedPropertyId(propertyId);
    setMessages([]);
    setPage(1);
    setThreadId(null);
    setIsDropdownOpen(false);
    setIsInitialLoading(true);
    setIsConversationStarted(false);
    setHasMore(true);
    setError(null);
    setHostContact(null); // Clear host contact when changing properties
    setIsActionsDropdownOpen(false); // Close actions dropdown when changing properties
  };

  const handlePredefinedQuestion = (question) => {
    setInput(question);
    sendMessage(question);
  };

  // Reset the guest's conversation by minting a fresh device id and starting
  // a new thread. Old conversation rows remain in Mongo (orphaned) but the
  // guest sees a clean slate and cannot accidentally reach the prior thread.
  const handleResetGuestConversation = async () => {
    if (!isGuest || !selectedPropertyId) return;
    const newId = uuidv4();
    localStorage.setItem('guestDeviceId', newId);
    setGuestDeviceId(newId);
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setIsConversationStarted(false);
    setIsActionsDropdownOpen(false);
    setThreadId(null);

    // Inline the /start call with the explicit new id — startNewThread()
    // would read the stale guestDeviceId from its closure.
    try {
      const response = await ai.get('/start', {
        params: {
          propertyId: selectedPropertyId,
          userId: null,
          deviceId: newId,
        },
        headers: { Authorization: `Bearer ${guestToken}` },
      });
      setThreadId(response.data.thread_id);
    } catch (error) {
      console.error('Failed to start fresh conversation:', error.response?.data?.error || error.message);
    }
  };

  const openWhatsApp = () => {
    if (!hostContact?.phone) return;

    // Format phone number for WhatsApp (remove non-digits and ensure it starts with country code)
    let phone = hostContact.phone.replace(/\D/g, '');

    // If phone doesn't start with country code, assume it's US and add +1
    if (!phone.startsWith('1') && phone.length === 10) {
      phone = '1' + phone;
    }

    const message = encodeURIComponent(`Hi ${hostContact.name}, I have a question about the property.`);
    const whatsappUrl = `https://wa.me/${phone}?text=${message}`;

    window.open(whatsappUrl, '_blank');
  };

  // Render a message based on its type
  const renderMessage = (message) => {
    switch (message.type) {
      case 'user':
        return (
          <span className="inline-block p-3 rounded-lg bg-indigo-600 text-white max-w-[75%]">
            {message.content}
          </span>
        );
      case 'ai':
        return (
          <span className="inline-block p-3 rounded-lg bg-gray-700 text-white max-w-[75%]">
            {message.content}
          </span>
        );
      case 'system':
        return (
          <span className="inline-block p-3 rounded-lg bg-yellow-600 text-white max-w-[75%]">
            {message.content}
          </span>
        );
      case 'error':
        return (
          <span className="inline-block p-3 rounded-lg bg-red-600 text-white max-w-[75%]">
            {message.content}
          </span>
        );
      case 'tour':
        return <TourMessage message={message} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      {/* Mobile Header */}
      <MobileHeader
        isOpen={isMobileMenuOpen}
        setIsOpen={setIsMobileMenuOpen}
        selectedPropertyId={selectedPropertyId}
        properties={properties}
        onPropertySelect={handlePropertyChange}
        isGuest={isGuest}
        guestPropertyName={guestPropertyName}
      />
  
      {/* Mobile Menu */}
      {!isGuest && (
        <MobileMenu
          isOpen={isMobileMenuOpen}
          setIsOpen={setIsMobileMenuOpen}
          userRole={isGuest ? 'guest' : 'user'}
        />
      )}
  
      {/* Property Selector - Hide on mobile */}
      <div className="hidden md:block fixed top-16 right-4 z-50">
        <div ref={dropdownRef} className="relative">
          {!isGuest ? (
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="bg-gray-800 text-white px-4 py-2 rounded-md flex items-center justify-between w-48 shadow-lg mt-4"
            >
              {selectedPropertyId 
                ? properties.find(p => p._id === selectedPropertyId)?.name || 'Select Property'
                : 'Select Property'}
              <FaChevronDown className={`ml-2 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`} />
            </button>
          ) : (
            <div className="bg-gray-800 text-white px-4 py-2 rounded-md flex items-center justify-between w-48 shadow-lg mt-4">
              {guestPropertyName}
            </div>
          )}
          {!isGuest && isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg overflow-hidden z-50">
              {properties.map(property => (
                <button
                  key={property._id}
                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700"
                  onClick={() => handlePropertyChange(property._id)}
                >
                  {property.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
  
      <div className="flex-grow overflow-hidden">
        <div
          id="scrollableDiv"
          className="h-full overflow-auto p-4 pt-8 md:pt-16 custom-scrollbar"
          style={{
            height: 'calc(100vh - 80px)', /* Account for header and footer */
            paddingBottom: '80px', /* Extra space for the fixed input field */
            scrollbarWidth: 'thin',
            scrollbarColor: '#4B5563 #1F2937'
          }}
        >
          <div className="max-w-3xl mx-auto w-full min-h-full flex flex-col">
            {isInitialLoading ? (
              <div className="flex flex-col justify-center items-center h-full">
                <FaSpinner className="animate-spin text-white text-4xl mb-4" />
                <p className="text-white text-lg">{t('chat.loading')}</p>
              </div>
            ) : error ? (
              <div className="flex flex-col justify-center items-center h-full">
                <p className="text-red-500 text-lg mb-4">{error}</p>
                <button 
                  onClick={fetchOrCreateThread} 
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                >
                  {t('chat.retry')}
                </button>
              </div>
            ) : messages.length === 0 && !isFetchingMessages ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="flex flex-col items-center w-full px-4">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 md:mb-8">{t('chat.welcome')}</h2>
                  <div className="w-full max-w-lg mb-4 md:mb-8">
                    <form onSubmit={handleSubmit} className="flex">
                      <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={t('chat.inputPlaceholder')}
                        className="flex-grow p-3 md:p-4 bg-gray-700 text-white border border-gray-600 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm md:text-lg"
                        style={{ fontSize: '16px' }}
                        disabled={isLoading || !selectedPropertyId}
                      />
                      <button
                        type="submit"
                        className={`p-3 md:p-4 ${isLoading ? 'bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'} text-white rounded-r-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        disabled={isLoading || !selectedPropertyId}
                      >
                        {isLoading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane className="text-xl" />}
                      </button>
                    </form>
                  </div>
                  
                  <div className="w-full max-w-lg overflow-x-auto mb-4 mt-2 hide-scrollbar">
                    <div className="flex space-x-2">
                      {predefinedQuestions.map((question) => (
                        <button
                          key={question}
                          onClick={() => handlePredefinedQuestion(question)}
                          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition duration-300 whitespace-nowrap flex-shrink-0"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Load More button at the top */}
                {hasMore && !isFetchingMessages && messages.length > 0 && (
                  <div className="text-center py-4">
                    <button
                      onClick={fetchMessages}
                      className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      {t('chat.loadMore')}
                    </button>
                  </div>
                )}

                {isFetchingMessages && hasMore && (
                  <div className="text-center py-4">
                    <FaSpinner className="animate-spin text-white text-2xl inline-block" />
                  </div>
                )}

                {!hasMore && messages.length > 0 && (
                  <p className="text-gray-500 text-center py-4">
                    {t('chat.noMoreMessages')}
                  </p>
                )}

                <div className="space-y-4 flex-grow">
                  {messages.map((message) => (
                    <div
                      key={message.id || `${message.type}-${message.timestamp}`}
                      className={`${message.type === 'user' ? 'text-right' : message.type === 'tour' ? 'text-center' : 'text-left'}`}
                    >
                      {renderMessage(message)}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
  
      {/* Input field footer */}
      {(isConversationStarted || messages.length > 0) && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gray-900 border-t border-gray-700 z-10">
          <div className="max-w-3xl mx-auto relative">
            <form onSubmit={handleSubmit} className="flex">
              {/* Plus button for actions */}
              <div ref={actionsDropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                  className="px-3 py-[14px] bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border border-gray-700 rounded-l transition duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={!selectedPropertyId}
                >
                  <FaPlus className={`transition-transform duration-200 ${isActionsDropdownOpen ? 'rotate-45' : ''}`} />
                </button>

                {/* Actions dropdown */}
                {isActionsDropdownOpen && (
                  <div className="absolute bottom-full mb-1 left-0 bg-gray-800 rounded-lg shadow-lg overflow-hidden z-20 whitespace-nowrap">
                    {hostContact?.phone && (
                      <button
                        onClick={() => {
                          openWhatsApp();
                          setIsActionsDropdownOpen(false);
                        }}
                        className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                      >
                        <FaWhatsapp className="text-green-400" />
                        <span>Contact {hostContact.name}</span>
                      </button>
                    )}
                    {isGuest && (
                      <button
                        onClick={handleResetGuestConversation}
                        className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                      >
                        <FaSyncAlt className="text-indigo-400" />
                        <span>{t('chat.resetConversation') || 'Reset conversation'}</span>
                      </button>
                    )}
                    {!isGuest && !hostContact?.phone && (
                      <div className="px-4 py-2 text-gray-400 text-sm">
                        No contact options available
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.inputPlaceholder')}
                className="flex-grow p-2 bg-gray-800 text-white border-t border-b border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={!selectedPropertyId}
              />
              <button
                type="submit"
                className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-r border border-indigo-600 transition duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={!selectedPropertyId}
              >
                <FaPaperPlane />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIChat;
